import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useCallback, useMemo } from 'react'

/** Id of the element a media tile animates from when its preview is expanded. */
export const mediaElementId = (fileId: string) => `file-${fileId}`

export const mediaElementSelector = (fileId: string) => `[data-testid="${mediaElementId(fileId)}"]`

// EXPL: only the tiles inside the DndContext may use `useSortable` — calling it outside silently
// falls back to dnd-kit's default context, which registers nothing but still hands out drag
// listeners and a `grab` cursor for an item that cannot be dragged.
export function useSortableWrapperProps(id: string) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  // EXPL: memoized because these props are spread onto a memoized tile — a fresh object on every
  // render fails the shallow compare and re-renders the tile for nothing. dnd-kit hands out
  // memoized `listeners`/`attributes` and a stable `setNodeRef`, so this holds while idle.
  return useMemo(() => ({
    ...listeners,
    ...attributes,
    ref: setNodeRef,
    style: {
      transform: CSS.Translate.toString(transform),
      transition,
      zIndex: isDragging ? '3' : '2',
      cursor: isDragging ? 'grabbing' : 'grab'
    }
  }), [listeners, attributes, setNodeRef, transform, transition, isDragging])
}

export type SortableWrapperProps = ReturnType<typeof useSortableWrapperProps>

export type MediaTileCallbacks = {
  onMetadataLoaded?: (fileId: string, orientation: 'landscape' | 'portrait', width: number, height: number) => void
  onSizeFetched?: (fileId: string, size: number) => void
}

/** Binds the tile's file id to the <FileInfo> callbacks, which report metadata without it. */
export function useFileInfoCallbacks(fileId: string, { onMetadataLoaded, onSizeFetched }: MediaTileCallbacks) {
  const metadataLoaded = useCallback((orientation: 'landscape' | 'portrait', width: number, height: number) => {
    if (onMetadataLoaded) {
      onMetadataLoaded(fileId, orientation, width, height)
    }
  }, [fileId, onMetadataLoaded])

  const sizeFetched = useCallback((size: number) => {
    if (onSizeFetched) {
      onSizeFetched(fileId, size)
    }
  }, [fileId, onSizeFetched])

  return { metadataLoaded, sizeFetched }
}
